import { useState, useMemo } from 'react';
import axios from 'axios';
import { MagnifyingGlass } from '@phosphor-icons/react';
import RegistrationsTable from './RegistrationsTable';
import RegistrationDetailModal from './RegistrationDetailModal';
import ScreenshotLightbox from './ScreenshotLightbox';
import ConfirmModal from './ConfirmModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_COPY = {
  approve: { title: 'Approve Registration?', body: 'The player will see this as Approved on their dashboard.', label: 'Approve', variant: 'success' },
  reject: { title: 'Reject Registration?', body: 'The player will see this as Rejected on their dashboard.', label: 'Reject', variant: 'danger' },
  delete: { title: 'Delete Registration?', body: 'This permanently removes the registration record. This action cannot be undone.', label: 'Delete', variant: 'danger' }
};

const buildSearchHaystack = (r) =>
  `${r.tournament_name} ${r.user_name} ${r.team_name || ''} ${r.captain_name || ''} ${r.phone || ''}`.toLowerCase();

const AdminRegistrationsTab = ({ registrations, search, setSearch, onRefresh }) => {
  const [detail, setDetail] = useState(null);
  const [screenshotUrl, setScreenshotUrl] = useState('');
  const [confirm, setConfirm] = useState(null); // { type, reg }
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    if (!search) return registrations;
    const needle = search.toLowerCase();
    return registrations.filter((r) => buildSearchHaystack(r).includes(needle));
  }, [registrations, search]);

  const openDetail = async (rid) => {
    try {
      const { data } = await axios.get(`${API}/admin/registrations/${rid}`, { withCredentials: true });
      setDetail(data);
    } catch (e) {
      window.alert('Failed to load registration');
    }
  };

  const requestAction = (type, reg) => setConfirm({ type, reg });

  const runAction = async () => {
    if (!confirm) return;
    setBusy(true);
    const { type, reg } = confirm;
    const endpoints = {
      approve: () => axios.post(`${API}/admin/registrations/${reg.registration_id}/approve`, {}, { withCredentials: true }),
      reject: () => axios.post(`${API}/admin/registrations/${reg.registration_id}/reject`, {}, { withCredentials: true }),
      delete: () => axios.delete(`${API}/admin/registrations/${reg.registration_id}`, { withCredentials: true })
    };
    try {
      await endpoints[type]();
      setConfirm(null);
      setDetail(null);
      onRefresh && onRefresh();
    } catch (e) {
      window.alert(e.response?.data?.detail || `Failed to ${type}`);
    } finally {
      setBusy(false);
    }
  };

  const copy = confirm ? ACTION_COPY[confirm.type] : null;

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="relative">
        <MagnifyingGlass size={16} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by tournament, player, team, captain, phone..."
          className="w-full bg-white border border-gray-200 focus:border-[#F97316] focus:ring-2 focus:ring-orange-100 rounded-full pl-11 pr-4 py-2.5 text-sm outline-none"
          data-testid="registrations-search-input"
        />
      </div>

      <RegistrationsTable
        registrations={filtered}
        onOpenDetail={openDetail}
        onRequestAction={requestAction}
      />

      <RegistrationDetailModal
        detail={detail}
        onClose={() => setDetail(null)}
        onOpenScreenshot={setScreenshotUrl}
        onAction={requestAction}
      />

      <ScreenshotLightbox url={screenshotUrl} onClose={() => setScreenshotUrl('')} />

      <ConfirmModal
        open={!!confirm}
        title={copy?.title || ''}
        description={copy?.body || ''}
        confirmLabel={copy?.label || 'Confirm'}
        variant={copy?.variant || 'danger'}
        onCancel={() => setConfirm(null)}
        onConfirm={runAction}
        busy={busy}
        testId="admin-registration-confirm-modal"
      />
    </div>
  );
};

export default AdminRegistrationsTab;
